import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Link2, FileUp, Type, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const previewUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/marketing/resources/${id}/preview`;
const emptyForm = { title: "", description: "", category: "", mode: "file", external_url: "", body_text: "" };

const AdminMarketing = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sort, setSort] = useState(null); // null (manual order) | "desc" | "asc" by downloads

  const cycleSort = () => setSort((s) => (s === null ? "desc" : s === "desc" ? "asc" : null));
  const displayItems = sort
    ? [...items].sort((a, b) => sort === "desc" ? (b.download_count || 0) - (a.download_count || 0) : (a.download_count || 0) - (b.download_count || 0))
    : items;

  const load = () => api.get("/admin/marketing/resources").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/marketing/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const inputCls = "bg-white border-[var(--ir-border)] rounded-none focus-visible:ring-[var(--ir-blue)]";
  const outlineBtn = "border-[var(--ir-border)] bg-white text-[var(--ir-blue)] hover:bg-[var(--ir-surface2)] rounded-none h-8";
  const dangerBtn = "border-rose-300 bg-white text-rose-600 hover:bg-rose-50 rounded-none h-8";

  const openAdd = () => { setForm({ ...emptyForm, category: cats[0] || "" }); setFile(null); setThumbnail(null); setDialog({ open: true, editing: null }); };
  const openEdit = (r) => {
    setForm({ title: r.title || "", description: r.description || "", category: r.category, mode: r.kind, external_url: r.external_url || "", body_text: r.body_text || "" });
    setFile(null); setThumbnail(null); setDialog({ open: true, editing: r });
  };

  const save = async () => {
    if (!form.title || !form.category) { toast.error("Title and category are required"); return; }
    setSaving(true);
    try {
      if (dialog.editing) {
        await api.patch(`/admin/marketing/resources/${dialog.editing.id}`, {
          title: form.title, description: form.description, category: form.category,
          external_url: form.mode === "link" ? form.external_url : "",
          body_text: form.mode === "text" ? form.body_text : "",
        });
        if (file || thumbnail) {
          const fd = new FormData();
          if (file) fd.append("file", file);
          if (thumbnail) fd.append("thumbnail", thumbnail);
          await api.post(`/admin/marketing/resources/${dialog.editing.id}/replace-file`, fd);
        }
        toast.success("Resource updated");
      } else {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("category", form.category);
        if (form.mode === "link") fd.append("external_url", form.external_url);
        if (form.mode === "text") fd.append("body_text", form.body_text);
        if (form.mode === "file" && file) fd.append("file", file);
        if (thumbnail) fd.append("thumbnail", thumbnail);
        await api.post("/admin/marketing/resources", fd);
        toast.success("Resource added");
      }
      setDialog({ open: false, editing: null }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (r) => {
    try { await api.patch(`/admin/marketing/resources/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const move = async (r, direction) => {
    try { await api.post(`/admin/marketing/resources/${r.id}/move?direction=${direction}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/marketing/resources/${r.id}`); toast.success("Resource deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-marketing">
      <div className="flex justify-between items-center mb-4">
        <p className="text-[var(--ir-muted)] text-sm">Upload logos, banners, swipe copy and more for affiliates. Videos are best added as external links.</p>
        <Button onClick={openAdd} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none" data-testid="add-resource-btn"><Plus className="w-4 h-4 mr-2" />Add Resource</Button>
      </div>
      <div className="bg-white border border-[var(--ir-border)] rounded-lg overflow-x-auto soft-shadow">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--ir-border)] hover:bg-transparent">
              <TableHead className="text-[var(--ir-muted)]">Order</TableHead>
              <TableHead className="text-[var(--ir-muted)]">Resource</TableHead>
              <TableHead className="text-[var(--ir-muted)]">Category</TableHead>
              <TableHead className="text-[var(--ir-muted)]">Type</TableHead>
              <TableHead className="text-[var(--ir-muted)] text-center">
                <button onClick={cycleSort} className="inline-flex items-center gap-1 hover:text-[var(--ir-blue)] transition-colors" data-testid="sort-downloads-btn" title="Sort by downloads">
                  Downloads
                  {sort === "desc" ? <ArrowDown className="w-3.5 h-3.5" /> : sort === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowUpDown className="w-3.5 h-3.5 opacity-50" />}
                </button>
              </TableHead>
              <TableHead className="text-[var(--ir-muted)] text-center">Active</TableHead>
              <TableHead className="text-[var(--ir-muted)] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow className="border-[var(--ir-border)]"><TableCell colSpan={7} className="text-center text-[var(--ir-muted)] py-10">No resources yet.</TableCell></TableRow>}
            {displayItems.map((r, i) => (
              <TableRow key={r.id} className="border-[var(--ir-border)]" data-testid={`resource-row-${r.id}`}>
                <TableCell>
                  <div className="flex flex-col">
                    <button onClick={() => move(r, "up")} disabled={i === 0 || sort !== null} title={sort ? "Clear the Downloads sort to reorder" : "Move up"} className="text-[var(--ir-muted)] hover:text-[var(--ir-blue)] disabled:opacity-30" data-testid={`move-up-${r.id}`}><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => move(r, "down")} disabled={i === displayItems.length - 1 || sort !== null} title={sort ? "Clear the Downloads sort to reorder" : "Move down"} className="text-[var(--ir-muted)] hover:text-[var(--ir-blue)] disabled:opacity-30" data-testid={`move-down-${r.id}`}><ChevronDown className="w-4 h-4" /></button>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded border border-[var(--ir-border)] bg-[var(--ir-surface2)] overflow-hidden flex items-center justify-center shrink-0">
                      {r.has_preview ? <img src={previewUrl(r.id)} alt="" className="w-full h-full object-cover" /> : <Type className="w-5 h-5 text-[var(--ir-muted)]" />}
                    </div>
                    <div>
                      <div className="text-[var(--ir-text)] text-sm font-medium">{r.title}</div>
                      {r.description && <div className="text-[var(--ir-muted)] text-xs max-w-[240px] truncate">{r.description}</div>}
                    </div>
                  </div>
                </TableCell>
                <TableCell><Badge className="rounded-none border bg-[var(--ir-blue)]/10 text-[var(--ir-blue)] border-[var(--ir-blue)]/25 text-[10px]">{r.category}</Badge></TableCell>
                <TableCell className="text-[var(--ir-muted)] text-xs capitalize">{r.kind}</TableCell>
                <TableCell className="text-center text-[var(--ir-text)] text-sm font-medium" data-testid={`downloads-${r.id}`}>{r.download_count ?? 0}</TableCell>
                <TableCell className="text-center"><Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`toggle-active-${r.id}`} /></TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)} className={outlineBtn} data-testid={`edit-resource-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(r)} className={dangerBtn} data-testid={`delete-resource-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="bg-white border-[var(--ir-border)] text-[var(--ir-text)] rounded-none max-w-lg" data-testid="resource-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{dialog.editing ? "Edit Resource" : "Add Resource"}</DialogTitle>
          <DialogDescription className="text-[var(--ir-muted)] text-xs">Upload a file, link an external video, or add swipe copy for affiliates.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="resource-title-input" /></div>
            <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} rows={2} data-testid="resource-desc-input" /></div>
            <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className={`${inputCls} h-10`} data-testid="resource-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c} value={c} data-testid={`resource-cat-opt-${c}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[var(--ir-muted)] text-xs mb-1 block">Content type</Label>
              <div className="grid grid-cols-3 gap-2">
                {[{ v: "file", i: FileUp, l: "File" }, { v: "link", i: Link2, l: "Link" }, { v: "text", i: Type, l: "Swipe copy" }].map((o) => (
                  <button key={o.v} onClick={() => setForm({ ...form, mode: o.v })} className={`flex items-center justify-center gap-1.5 py-2 text-xs border rounded-none transition-colors ${form.mode === o.v ? "bg-[var(--ir-blue)] text-white border-[var(--ir-blue)]" : "bg-white text-[var(--ir-text)] border-[var(--ir-border)] hover:bg-[var(--ir-surface2)]"}`} data-testid={`resource-mode-${o.v}`}><o.i className="w-3.5 h-3.5" />{o.l}</button>
                ))}
              </div>
            </div>
            {form.mode === "file" && (
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">File {dialog.editing && "(leave blank to keep current)"}</Label>
                <Input type="file" onChange={(e) => setFile(e.target.files[0] || null)} className={inputCls} data-testid="resource-file-input" />
                <p className="text-[var(--ir-muted)] text-[11px] mt-1">Images, PDFs, docs, plain text. Max 50 MB. Executables/scripts blocked.</p>
              </div>
            )}
            {form.mode === "link" && (
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">External URL (e.g. YouTube/Vimeo)</Label><Input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://…" className={inputCls} data-testid="resource-url-input" /></div>
            )}
            {form.mode === "text" && (
              <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Swipe copy text</Label><Textarea value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} rows={4} className={inputCls} data-testid="resource-bodytext-input" /></div>
            )}
            <div><Label className="text-[var(--ir-muted)] text-xs mb-1 block">Custom thumbnail (optional image)</Label>
              <Input type="file" accept="image/*" onChange={(e) => setThumbnail(e.target.files[0] || null)} className={inputCls} data-testid="resource-thumbnail-input" />
              <p className="text-[var(--ir-muted)] text-[11px] mt-1">Image files preview themselves automatically. Add a thumbnail for PDFs, videos or swipe copy.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, editing: null })} className="border-[var(--ir-border)] bg-white text-[var(--ir-text)] rounded-none">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none disabled:opacity-50" data-testid="save-resource-btn">{saving ? "Saving…" : (dialog.editing ? "Save changes" : "Add resource")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMarketing;
